import React, { Component } from 'react'
import { connect } from 'react-redux'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import ImageHero from 'components/Common/ImageHero'
import Faq from 'components/Common/Faq'
import Image from 'components/Common/Image'
import Spacing from 'components/Common/Spacing'

import {
  Root,
  Logos,
  HeaderBottom,
  TextPanelContent,
  FinePrintLinks,
  JoinPanel,
  CommonHeaderTop,
  CommonMainHeaderBox, CommonStepBox,
  CommonStepNumber,
} from './../style'
import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import {
  alreadyLinked,
  getPartnershipPrefixUpperCase,
} from 'selectors/partnershipSelectors'
import HttpStatus from 'components/Common/HttpStatus'
import Error404Page from 'components/Pages/Error404Page'
import {
  accountAccessShowLogin,
  accountAccessShowPartnershipLink,
} from 'actions/UiActions'
import { isLoggedIn } from 'selectors/accountSelectors'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { promotionBanner } from 'analytics/promotions'
import { addGTMEvent } from 'api/googleTagManager'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'

const topPanelImageId = 'iStock-510412180_e6qtz2'
const bottomPanelImageId = 'shutterstock_284109371_x4swxr'

const ThreeColumn = styled.div`
  display: grid;
  gap: ${rem(12)};

  ${mediaQueryUp.desktop}{
    grid-template-columns: repeat(3, 1fr);
  }
`

interface Props {
  accountAccessShowPartnershipLink: () => void;
  accountAccessShowLogin: () => void;
  regionCode: string;
  linkPartnershipAccount: string;
  loggedIn?: boolean;
  alreadyLinked?: boolean;
  partnership: App.Partnership;
}

class KrisFlyerLandingPageContainer extends Component<React.PropsWithChildren<Props>> {
  onClickTrack = e => {
    const target = e.currentTarget
    const data = target.dataset
    if (data.bannerid) {
      const bannerId = data.bannerid
      addGTMEvent(promotionBanner({ bannerId, hitType: 'partnership-click' }))
    }
  }

  showJoin = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.onClickTrack(e)
    this.props.accountAccessShowLogin()
  }

  showLogin = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.onClickTrack(e)
    this.props.accountAccessShowLogin()
  }

  showKrisFlyerLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.onClickTrack(e)
    this.props.accountAccessShowPartnershipLink()
  }

  getButton(bannerID: string) {
    const {
      loggedIn,
      alreadyLinked,
    } = this.props

    if (loggedIn && alreadyLinked) {
      return <TextButton kind="primary" data-testid={bannerID} data-bannerid={bannerID} onClick={this.onClickTrack} href="/">
        Browse offers
      </TextButton>
    } else if (loggedIn) {
      return <TextButton kind="primary" data-testid={bannerID} data-bannerid={bannerID} onClick={this.showKrisFlyerLink}>
        Link your account
      </TextButton>
    } else {
      return <TextButton kind="primary" data-testid={bannerID} data-bannerid={bannerID} onClick={this.showJoin}>
        Join Luxury Escapes
      </TextButton>
    }
  }

  render() {
    const {
      regionCode,
      partnership,
    } = this.props

    if (!partnership || partnership.prefix !== 'kfp' || !partnership.regionCodes.includes(regionCode)) {
      return <HttpStatus code={404}>
        <Error404Page invalidRegion />
      </HttpStatus>
    }

    return (
      <Root>
        <HeadData
          title="KrisFlyer"
          description="Earn KrisFlyer miles on eligible holidays."
        />

        <ImageHero
          alt="KrisFlyer"
          id={topPanelImageId}
          gravity="center"
          mobileAspectRatio="0.8"
          tabletAspectRatio="1.5"
          desktopAspectRatio="2"
          largeDesktopAspectRatio="2.2"
        >
          <CommonHeaderTop>
            <LayoutContainer>
              <CommonMainHeaderBox>
                <Logos
                width={395}
                id="KrisFlyer-Luxury-Escapes_1hagwg"
                alt="Luxury Escapes & KrisFlyer Logos"
                highRes
              />
                <Heading variant="heading2" as="h1" format="uppercase">
                  Earn KrisFlyer miles <br /> on eligible escapes.
                </Heading>
                <Spacing mt={16}>
                  <BodyText variant="large">Until 28 Feb 2022, you can also earn Elite miles.</BodyText>
                </Spacing>
                <Spacing mt={24}>
                  {this.getButton('kfpAccountLink1')}
                </Spacing>
                <Spacing mt={24}>
                  <Heading variant="heading5" as="h1" format="uppercase">Unlock status credits without flying</Heading>
                  <Spacing mt={16}>
                    <BodyText variant="medium">
                      Until 28 February 2022, you will also earn 1 Elite mile<br />
                      for every 5 KrisFlyer miles earned on your escape. This will<br />
                      count towards your PPS Club or KrisFlyer Elite membership <br />
                      renewal that is due between March 2022 and February 2023, <br />
                      or to upgrade your membership status.
                    </BodyText>
                  </Spacing>
                </Spacing>
              </CommonMainHeaderBox>
            </LayoutContainer>
          </CommonHeaderTop>
        </ImageHero>
        <PaneBody size="xl-y">
          <TextPanelContent size="sm">
            <Heading variant="heading3">Luxury Escapes + KrisFlyer</Heading>
            <BodyText variant="large">We're committed to providing great deals to amazing places and now Luxury Escapes members can also earn KrisFlyer miles.</BodyText>
            {this.getButton('kfpAccountLink2')}
          </TextPanelContent>
        </PaneBody>
        <Pane>
          <PaneBody size="xl-y">
            <LayoutContainer>
              <ImageHero
              alt="KrisFlyer"
              id={bottomPanelImageId}
              gravity="center"
              tabletAspectRatio="2.1"
              desktopAspectRatio="3.1"
              largeDesktopAspectRatio="4.1"
            >
                <HeaderBottom>
                  <Heading variant="heading3" colour="neutral-eight">
                    Earn 1 KrisFlyer mile per $1 USD<br />spent on eligible holidays.
                  </Heading>
                </HeaderBottom>
              </ImageHero>
            </LayoutContainer>
            <PaneBody size="xl-y">
              <TextPanelContent>
                <Heading variant="heading2">Earn KrisFlyer miles</Heading>
                <ThreeColumn>
                  <CommonStepBox>
                    <CommonStepNumber>
                      <BodyText variant="medium" weight="bold">1</BodyText>
                    </CommonStepNumber>
                    <BodyText variant="large" weight="bold">Sign up and link</BodyText>
                    <BodyText variant="medium">Join Luxury Escapes, then follow the prompts to link your KrisFlyer account and Luxury Escapes accounts.</BodyText>
                  </CommonStepBox>
                  <CommonStepBox>
                    <CommonStepNumber>
                      <div>
                        <BodyText variant="medium" weight="bold">2</BodyText>
                      </div>
                    </CommonStepNumber>
                    <BodyText variant="large" weight="bold">Explore and book</BodyText>
                    <BodyText variant="medium">Select your dream destination and make sure you have added your KrisFlyer ID before checking out.</BodyText>
                  </CommonStepBox>
                  <CommonStepBox>
                    <CommonStepNumber>
                      <div>
                        <BodyText variant="medium" weight="bold">3</BodyText>
                      </div>
                    </CommonStepNumber>
                    <BodyText variant="large" weight="bold">Earn KrisFlyer miles</BodyText>
                    <BodyText variant="medium">Earn 1 KrisFlyer mile per $1 USD spent on eligible holidays.</BodyText>
                  </CommonStepBox>
                  <CommonStepBox>
                    <CommonStepNumber>
                      <div>
                        <BodyText variant="medium" weight="bold">4</BodyText>
                      </div>
                    </CommonStepNumber>
                    <BodyText variant="large" weight="bold">Unlock status credits without flying</BodyText>
                    <BodyText variant="medium">Until 28 February 2022, you will also earn 1 Elite mile for every 5 KrisFlyer miles earned on your escape.</BodyText>
                  </CommonStepBox>
                </ThreeColumn>
              </TextPanelContent>
            </PaneBody>
          </PaneBody>
        </Pane>
        <JoinPanel>
          <TextPanelContent>
            <Image
            highRes
            width={172}
            id="krisFlyerLogo_2x_md64qq"
            alt="KrisFlyer Logo"
          />
            <BodyText variant="large" weight="bold">Not a KrisFlyer member?</BodyText>
            <BodyText variant="large">Join now for free</BodyText>
            <TextButton
            kind="primary"
            onClick={this.onClickTrack}
            data-bannerid="kfpJoin"
            data-testid="singapore-airlines-destination"
            href="https://www.singaporeair.com/en_UK/ppsclub-krisflyer/registration-form/"
            target="_blank"
          >
              Join KrisFlyer
            </TextButton>
            <FinePrintLinks>
              <TextLink to="/terms-and-conditions#krisFlyer" data-bannerid="kfpTnC" onClick={this.onClickTrack}>
                Terms & Conditions
              </TextLink>
            </FinePrintLinks>
          </TextPanelContent>
        </JoinPanel>
        <Pane>
          <PaneBody size="xl-y">
            <LayoutContainer>
              <Heading variant="heading3" align="center" as="h3">Frequently Asked Questions</Heading>
              <Spacing mt={40}>
                <BodyText variant="large" align="start">
                  <Faq maxToShow={10} slug="kfp-faq"/>
                </BodyText>
              </Spacing>
            </LayoutContainer>
          </PaneBody>
        </Pane>
      </Root>
    )
  }
}

const mapStateToProps = (state: App.State) => ({
  loggedIn: isLoggedIn(state),
  alreadyLinked: alreadyLinked(state),
  regionCode: state.geo.currentRegionCode,
  partnership: state.geo.partnership,
  linkPartnershipAccount: `${getPartnershipPrefixUpperCase(state)}Link`,
})

const mapDispatchToProps = {
  accountAccessShowLogin,
  accountAccessShowPartnershipLink,
}

export default connect(mapStateToProps, mapDispatchToProps)(KrisFlyerLandingPageContainer)
